import { useState } from 'react';
import { Funnel, MagnifyingGlass, X } from '@phosphor-icons/react';
import { Button } from '@/components/ui-kit/button';
import { Checkbox } from '@/components/ui-kit/checkbox';
import { Input } from '@/components/ui-kit/input';
import { Label } from '@/components/ui-kit/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui-kit/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui-kit/select';
import type {
  CampaignChannelFilter,
  CampaignDisplayStatus,
  CampaignsFilters,
  CampaignsSortKey,
} from '../../types/campaigns.types';
import {
  CAMPAIGN_CHANNEL_FILTER_OPTIONS,
  CAMPAIGN_DISPLAY_STATUS_OPTIONS,
  CAMPAIGNS_SORT_OPTIONS,
} from '../../constants/campaigns.constants';

type CampaignsFiltersBarProps = {
  search: string;
  filters: CampaignsFilters;
  sortKey: CampaignsSortKey;
  onSearchChange: (value: string) => void;
  onFiltersChange: (filters: CampaignsFilters) => void;
  onSortKeyChange: (sortKey: CampaignsSortKey) => void;
};

export const CampaignsFiltersBar = ({
  search,
  filters,
  sortKey,
  onSearchChange,
  onFiltersChange,
  onSortKeyChange,
}: CampaignsFiltersBarProps) => {
  const [statusPanelOpen, setStatusPanelOpen] = useState(false);
  const [draftStatuses, setDraftStatuses] = useState<CampaignDisplayStatus[]>(filters.statuses);

  const handleStatusPanelOpenChange = (open: boolean) => {
    if (open) setDraftStatuses(filters.statuses);
    setStatusPanelOpen(open);
  };

  const toggleDraftStatus = (status: CampaignDisplayStatus, checked: boolean) => {
    setDraftStatuses((current) =>
      checked
        ? current.includes(status)
          ? current
          : [...current, status]
        : current.filter((value) => value !== status)
    );
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="flex flex-wrap items-center gap-3">
        <div className="relative min-w-[220px] flex-1">
          <MagnifyingGlass
            className="pointer-events-none absolute left-3 top-1/2 size-[18px] -translate-y-1/2 text-muted-foreground"
            aria-hidden
          />
          <Input
            value={search}
            onChange={(event) => onSearchChange(event.target.value)}
            placeholder="Search campaigns by name or subject"
            className="h-10 rounded-md border-input pl-10 pr-10 text-sm"
            aria-label="Search campaigns by name or subject"
          />
          {search ? (
            <button
              type="button"
              className="absolute right-2 top-1/2 flex size-8 -translate-y-1/2 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
              aria-label="Clear campaign search"
              onClick={() => onSearchChange('')}
            >
              <X className="size-4" aria-hidden />
            </button>
          ) : null}
        </div>

        <Popover open={statusPanelOpen} onOpenChange={handleStatusPanelOpenChange}>
          <PopoverTrigger asChild>
            <Button
              type="button"
              variant="outline"
              className="h-10 shrink-0 gap-2 px-4 text-sm font-medium"
              aria-label={`Status filters${
                filters.statuses.length > 0 ? `, ${filters.statuses.length} selected` : ''
              }`}
            >
              <Funnel className="size-4" aria-hidden />
              Status
              {filters.statuses.length > 0 ? (
                <span className="ml-0.5 rounded-full bg-primary-50 px-1.5 text-xs font-medium text-primary-600">
                  {filters.statuses.length}
                </span>
              ) : null}
            </Button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-64 p-0">
            <div className="p-4">
              <p className="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">
                Status
              </p>
              <div className="flex flex-col gap-2">
                {CAMPAIGN_DISPLAY_STATUS_OPTIONS.map((option) => {
                  const id = `campaign-status-filter-${option.value.toLowerCase()}`;
                  return (
                    <div key={option.value} className="flex items-center gap-2">
                      <Checkbox
                        id={id}
                        checked={draftStatuses.includes(option.value)}
                        onCheckedChange={(checked) =>
                          toggleDraftStatus(option.value, checked === true)
                        }
                      />
                      <Label htmlFor={id} className="cursor-pointer text-sm font-normal">
                        {option.label}
                      </Label>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="flex items-center justify-between border-t bg-popover px-4 py-3">
              <button
                type="button"
                className="text-xs font-medium text-muted-foreground hover:text-foreground"
                onClick={() => setDraftStatuses([])}
              >
                Clear all
              </button>
              <button
                type="button"
                className="text-xs font-medium text-primary-600 hover:text-primary-700"
                onClick={() => {
                  onFiltersChange({ ...filters, statuses: draftStatuses });
                  setStatusPanelOpen(false);
                }}
              >
                Done
              </button>
            </div>
          </PopoverContent>
        </Popover>

        <Select
          value={filters.channel ?? 'all'}
          onValueChange={(value) =>
            onFiltersChange({ ...filters, channel: value as CampaignChannelFilter })
          }
        >
          <SelectTrigger className="h-10 w-[9rem]" aria-label="Filter campaigns by channel">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {CAMPAIGN_CHANNEL_FILTER_OPTIONS.map((option) => (
              <SelectItem key={option.value} value={option.value}>
                {option.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <label className="flex items-center gap-2 text-xs text-muted-foreground">
          Sort
          <Select
            value={sortKey}
            onValueChange={(value) => onSortKeyChange(value as CampaignsSortKey)}
          >
            <SelectTrigger className="h-10 w-[12rem]" aria-label="Sort campaigns">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {CAMPAIGNS_SORT_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </label>
      </div>

      {filters.statuses.length > 0 ? (
        <div className="flex flex-wrap items-center gap-2">
          {filters.statuses.map((status) => (
            <span
              key={status}
              className="inline-flex items-center gap-1 rounded-full bg-primary-50 px-2.5 py-1 text-xs font-medium text-primary-600"
            >
              {status}
              <button
                type="button"
                className="flex size-4 items-center justify-center rounded-full hover:bg-primary-100"
                aria-label={`Remove ${status} filter`}
                onClick={() =>
                  onFiltersChange({
                    ...filters,
                    statuses: filters.statuses.filter((value) => value !== status),
                  })
                }
              >
                <X className="size-3" aria-hidden />
              </button>
            </span>
          ))}
        </div>
      ) : null}
    </div>
  );
};
